import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import axios from "../api/axios";
import { getHighestRole } from "../utils/auth";

const panelClass =
  "relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900";
const fieldClass =
  "mt-2 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-slate-900 focus:ring-4 focus:ring-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-50 dark:focus:border-slate-200 dark:focus:ring-slate-900";
const primaryButtonClass =
  "inline-flex w-full items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-50 dark:text-slate-950 dark:hover:bg-slate-200";
const secondaryButtonClass =
  "inline-flex w-full items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-3 font-semibold text-slate-900 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-50 dark:hover:bg-slate-700";

function Login() {
  const [signupEmail, setSignupEmail] = useState("");
  const [signupOtp, setSignupOtp] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [signupStep, setSignupStep] = useState("email");
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupTempToken, setSignupTempToken] = useState(null);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Forgot password state
  const [loginMode, setLoginMode] = useState("login"); // "login" | "forgot_email" | "forgot_otp"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotPasswordState, setForgotPasswordState] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const navigate = useNavigate();

  const sendForgotOTP = async () => {
    if (!forgotEmail.trim()) {
      return alert("Please enter your email address");
    }
    setForgotLoading(true);
    try {
      await axios.post("/auth/forgot-password", { email: forgotEmail });
      alert("OTP sent to your email. Enter it to reset your password.");
      setLoginMode("forgot_otp");
    } catch (error) {
      alert("Failed to send OTP: " + (error.response?.data?.message || error.message));
    } finally {
      setForgotLoading(false);
    }
  };

  const resetPassword = async () => {
    if (!forgotOtp.trim()) {
      return alert("Please enter the OTP");
    }
    if (!forgotPasswordState || !forgotConfirmPassword) {
      return alert("Please enter and confirm your new password");
    }
    if (forgotPasswordState !== forgotConfirmPassword) {
      return alert("Passwords do not match");
    }
    if (forgotPasswordState.length < 6) {
      return alert("Password must be at least 6 characters");
    }
    setForgotLoading(true);
    try {
      const res = await axios.post("/auth/reset-password", {
        email: forgotEmail,
        otp: forgotOtp.trim().replace(/\s+/g, ""),
        password: forgotPasswordState,
      });
      alert(res.data.message || "Password reset successfully. Please login with your new password.");
      setLoginMode("login");
      setLoginEmail(forgotEmail);
      setLoginPassword("");
      // Clear fields
      setForgotEmail("");
      setForgotOtp("");
      setForgotPasswordState("");
      setForgotConfirmPassword("");
    } catch (error) {
      alert("Failed to reset password: " + (error.response?.data?.message || error.message));
    } finally {
      setForgotLoading(false);
    }
  };
  const { isDarkMode, toggleTheme } = useTheme();

  const storeAuthSession = (data, email) => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("email", email);
    localStorage.setItem("role", data.role || "general");
    // Store the full roles list (multi-role). Fall back to the single role.
    const roles = Array.isArray(data.roles) && data.roles.length
      ? data.roles
      : [data.role || "general"];
    localStorage.setItem("roles", JSON.stringify(roles));
    localStorage.setItem("department_id", data.department_id || "");
    localStorage.setItem("is_moderator", data.is_moderator ? "true" : "false");
    // Default the active role to the user's HIGHEST-privilege role so a
    // multi-role user (e.g. super_admin + hr + faculty) starts as super_admin.
    localStorage.setItem("activeRole", getHighestRole(roles));
  };

  const resetSignup = () => {
    setSignupEmail("");
    setSignupOtp("");
    setSignupPassword("");
    setSignupConfirmPassword("");
    setSignupStep("email");
    setSignupTempToken(null);
  };

  const resetLogin = () => {
    setLoginEmail("");
    setLoginPassword("");
  };

  const sendOTP = async () => {
    if (!signupEmail.trim()) {
      return alert("Please enter your email address");
    }

    setSignupLoading(true);
    try {
      await axios.post("/auth/send-otp", { email: signupEmail });
      alert("OTP sent to your email. Enter it to continue signup.");
      setSignupStep("otp");
    } catch (error) {
      alert("Failed to send OTP: " + (error.response?.data?.message || error.message));
    } finally {
      setSignupLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!signupOtp.trim()) {
      return alert("Please enter the OTP");
    }

    setSignupLoading(true);
    try {
      const res = await axios.post("/auth/verify-otp", {
        email: signupEmail,
        otp: signupOtp.trim().replace(/\s+/g, ""),
      });

      if (res.data.requiresPasswordSet) {
        setSignupTempToken(res.data.tempToken);
        setSignupStep("password");
        return;
      }

      alert(res.data.message || "OTP verified");
    } catch (error) {
      const apiMessage = error.response?.data?.message || error.message;

      if (apiMessage === "This account has a password. Please use login instead.") {
        setLoginEmail(signupEmail);
        alert(apiMessage);
        return;
      }

      alert(apiMessage);
    } finally {
      setSignupLoading(false);
    }
  };

  const setPasswordAfterOTP = async () => {
    if (!signupPassword || !signupConfirmPassword) {
      return alert("Please create and confirm a password");
    }

    if (signupPassword !== signupConfirmPassword) {
      return alert("Passwords do not match");
    }

    if (signupPassword.length < 6) {
      return alert("Password must be at least 6 characters");
    }

    setSignupLoading(true);
    try {
      const res = await axios.post(
        "/auth/set-password",
        {
          password: signupPassword,
          confirmPassword: signupConfirmPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${signupTempToken}`,
          },
        }
      );

      storeAuthSession(res.data, signupEmail);
      alert("Password set successfully. Redirecting to your dashboard.");
      navigate("/dashboard");
    } catch (error) {
      alert("Failed to set password: " + (error.response?.data?.message || error.message));
    } finally {
      setSignupLoading(false);
    }
  };

  const loginWithPassword = async () => {
    if (!loginEmail.trim() || !loginPassword) {
      return alert("Please enter your email and password");
    }

    setLoginLoading(true);
    try {
      const res = await axios.post("/auth/login", {
        email: loginEmail,
        password: loginPassword,
      });

      storeAuthSession(res.data, loginEmail);
      navigate("/dashboard");
    } catch (error) {
      alert("Invalid email or password: " + (error.response?.data?.message || error.message));
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.12),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(16,185,129,0.10),_transparent_26%),linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)] px-4 py-8 text-slate-900 dark:bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.12),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(34,197,94,0.10),_transparent_26%),linear-gradient(180deg,_#020617_0%,_#0f172a_100%)] dark:text-slate-50 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute -left-24 top-24 h-64 w-64 rounded-full bg-sky-300/20 blur-3xl dark:bg-sky-500/10" />
      <div className="pointer-events-none absolute -right-20 bottom-10 h-72 w-72 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-500/10" />
      <div className="absolute right-4 top-4 z-10">
        <button
          onClick={toggleTheme}
          className="rounded-full border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900"
          title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          {isDarkMode ? (
            <svg className="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.536l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.121-10.607a1 1 0 010 1.414l-.707.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zm5.414 5.414a1 1 0 01.707.293l.707-.707a1 1 0 11-1.414-1.414l-.707.707a1 1 0 01.707.121zM5 8a1 1 0 100-2H4a1 1 0 000 2h1z" />
            </svg>
          ) : (
            <svg className="h-5 w-5 text-slate-700" fill="currentColor" viewBox="0 0 20 20">
              <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
            </svg>
          )}
        </button>
      </div>

      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 pt-14">
        <div className="flex flex-col items-center text-center">
          <img
            src="/svpcet.jpg"
            alt="St. Vincent Pallotti College of Engineering & Technology"
            className="h-20 w-20 rounded-full object-contain shadow-sm ring-1 ring-slate-200 dark:ring-slate-700 bg-white"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <h1 className="mt-4 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-50 sm:text-2xl max-w-2xl">
            ST. VINCENT PALLOTTI COLLEGE OF ENGINEERING &amp; TECHNOLOGY
          </h1>
          <div className="mx-auto mt-4 h-1 w-24 rounded-full bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400" />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <section className={panelClass}>
            <div className="absolute right-0 top-0 h-28 w-28 rounded-bl-full bg-gradient-to-br from-sky-500/10 to-transparent dark:from-sky-400/10" />
            <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400" />
            <h2 className="text-lg font-semibold">New User</h2>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Email</label>
                <input
                  type="email"
                  placeholder="Email address"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  className={fieldClass}
                  disabled={signupLoading}
                />
              </div>

              {signupStep !== "email" && (
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-300">OTP</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="OTP"
                    value={signupOtp}
                    onChange={(e) => setSignupOtp(e.target.value)}
                    className={fieldClass}
                    disabled={signupLoading}
                  />
                </div>
              )}

              {signupStep === "password" && (
                <>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Password</label>
                    <input
                      type="password"
                      placeholder="Password"
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      className={fieldClass}
                      disabled={signupLoading}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Confirm</label>
                    <input
                      type="password"
                      placeholder="Confirm password"
                      value={signupConfirmPassword}
                      onChange={(e) => setSignupConfirmPassword(e.target.value)}
                      className={fieldClass}
                      disabled={signupLoading}
                    />
                  </div>
                </>
              )}

              {signupStep === "email" && (
                <button onClick={sendOTP} className={primaryButtonClass} disabled={signupLoading}>
                  {signupLoading ? "Sending..." : "Send OTP"}
                </button>
              )}

              {signupStep === "otp" && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <button onClick={verifyOTP} className={primaryButtonClass} disabled={signupLoading}>
                    {signupLoading ? "Verifying..." : "Verify OTP"}
                  </button>
                  <button onClick={resetSignup} className={secondaryButtonClass} disabled={signupLoading}>
                    Reset
                  </button>
                </div>
              )}

              {signupStep === "password" && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <button onClick={setPasswordAfterOTP} className={primaryButtonClass} disabled={signupLoading}>
                    {signupLoading ? "Saving..." : "Set Password"}
                  </button>
                  <button onClick={resetSignup} className={secondaryButtonClass} disabled={signupLoading}>
                    Reset
                  </button>
                </div>
              )}
            </div>
          </section>

          <section className={panelClass}>
            <div className="absolute right-0 top-0 h-28 w-28 rounded-bl-full bg-gradient-to-br from-emerald-500/10 to-transparent dark:from-emerald-400/10" />
            <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-emerald-500 via-teal-400 to-sky-400" />
            
            {loginMode === "login" && (
              <>
                <h2 className="text-lg font-semibold">Login</h2>
                <div className="mt-5 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Email</label>
                    <input
                      type="email"
                      placeholder="Email address"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className={fieldClass}
                      disabled={loginLoading}
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center">
                      <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Password</label>
                      <button
                        onClick={() => {
                          setForgotEmail(loginEmail);
                          setLoginMode("forgot_email");
                        }}
                        className="text-xs font-semibold text-emerald-600 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300 outline-none transition-colors"
                      >
                        Forgot Password?
                      </button>
                    </div>
                    <input
                      type="password"
                      placeholder="Password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className={fieldClass}
                      disabled={loginLoading}
                    />
                  </div>

                  <button onClick={loginWithPassword} className={primaryButtonClass} disabled={loginLoading}>
                    {loginLoading ? "Logging in..." : "Login"}
                  </button>

                  <button onClick={resetLogin} className={secondaryButtonClass} disabled={loginLoading}>
                    Clear
                  </button>
                </div>
              </>
            )}

            {loginMode === "forgot_email" && (
              <>
                <h2 className="text-lg font-semibold">Forgot Password</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Enter your registered email address and we'll send you an OTP to reset your password.
                </p>
                <div className="mt-5 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Email</label>
                    <input
                      type="email"
                      placeholder="Email address"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className={fieldClass}
                      disabled={forgotLoading}
                    />
                  </div>

                  <button onClick={sendForgotOTP} className={primaryButtonClass} disabled={forgotLoading}>
                    {forgotLoading ? "Sending..." : "Send Reset OTP"}
                  </button>

                  <button
                    onClick={() => {
                      setLoginMode("login");
                      setForgotEmail("");
                    }}
                    className={secondaryButtonClass}
                    disabled={forgotLoading}
                  >
                    Back to Login
                  </button>
                </div>
              </>
            )}

            {loginMode === "forgot_otp" && (
              <>
                <h2 className="text-lg font-semibold">Reset Password</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Enter the OTP sent to your email and choose a new password.
                </p>
                <div className="mt-5 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300">OTP</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter OTP"
                      value={forgotOtp}
                      onChange={(e) => setForgotOtp(e.target.value)}
                      className={fieldClass}
                      disabled={forgotLoading}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300">New Password</label>
                    <input
                      type="password"
                      placeholder="New password (min 6 chars)"
                      value={forgotPasswordState}
                      onChange={(e) => setForgotPasswordState(e.target.value)}
                      className={fieldClass}
                      disabled={forgotLoading}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Confirm Password</label>
                    <input
                      type="password"
                      placeholder="Confirm new password"
                      value={forgotConfirmPassword}
                      onChange={(e) => setForgotConfirmPassword(e.target.value)}
                      className={fieldClass}
                      disabled={forgotLoading}
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <button onClick={resetPassword} className={primaryButtonClass} disabled={forgotLoading}>
                      {forgotLoading ? "Resetting..." : "Reset Password"}
                    </button>
                    <button
                      onClick={() => {
                        setLoginMode("forgot_email");
                        setForgotOtp("");
                        setForgotPasswordState("");
                        setForgotConfirmPassword("");
                      }}
                      className={secondaryButtonClass}
                      disabled={forgotLoading}
                    >
                      Back
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default Login;
